import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '../components/Icon'
import { api, get } from '../lib/api'
import { errorText, getLocale } from '../lib/i18n'

/**
 * Feedback: a button anyone signed in can press, a page to see what they sent and what became of it, and the owner's inbox.
 * The server side is services/api/src/market/feedback.ts. Colours come from the look through the same variables the tour uses.
 */
const L = (en: string, es: string) => (getLocale() === 'en' ? en : es)
type Kind = 'idea' | 'problem' | 'praise' | 'question'
type Status = 'new' | 'seen' | 'planned' | 'done' | 'wont'
const KINDS: Kind[] = ['idea', 'problem', 'praise', 'question']
const STATUSES: Status[] = ['new', 'seen', 'planned', 'done', 'wont']
const kindLabel = (k: Kind) => ({ idea: L('An idea', 'Una idea'), problem: L('Something is wrong', 'Algo no funciona'), praise: L('Something I like', 'Algo que me gusta'), question: L('A question', 'Una pregunta') })[k]
const statusLabel = (s: Status) => ({ new: L('Received', 'Recibido'), seen: L('We have read it', 'Lo hemos leído'), planned: L('Planned', 'Planeado'), done: L('Done', 'Hecho'), wont: L('Not for now', 'Por ahora no') })[s]
const day = (ms: number) => new Date(ms).toLocaleDateString(getLocale(), { month: 'short', day: 'numeric', year: 'numeric' })

const OPEN = 'cl:feedback'
/** Anywhere in the app can open the form. */
export const openFeedback = () => window.dispatchEvent(new Event(OPEN))

function Stars({ value, onPick, label }: { value: number | null; onPick?: (n: number | null) => void; label: string }) {
  return <div role={onPick ? 'radiogroup' : 'img'} aria-label={onPick ? label : `${value} / 5`} className="fb-stars">
    {[1, 2, 3, 4, 5].map(n => onPick
      ? <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={L(`${n} of 5`, `${n} de 5`)} className={`fb-star${(value ?? 0) >= n ? ' on' : ''}`} onClick={() => onPick(value === n ? null : n)}><Icon name="star" size={26}/></button>
      : <span key={n} className={`fb-star${(value ?? 0) >= n ? ' on' : ''}`}><Icon name="star" size={16}/></span>)}
  </div>
}

function Sheet({ onClose }: { onClose: () => void }) {
  const [kind, setKind] = useState<Kind | null>(null)
  const [rating, setRating] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const card = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', key)
    card.current?.querySelector<HTMLElement>('[role=radio], textarea, button')?.focus()
    return () => window.removeEventListener('keydown', key)
  }, [onClose])
  const send = async () => {
    if (!kind || busy) return
    setBusy(true); setError('')
    try {
      await api('/api/market/feedback', { method: 'POST', body: JSON.stringify({ kind, rating, message, page: location.hash, locale: getLocale() }) })
      setSent(true)
    } catch (e) { setError(errorText(e)) } finally { setBusy(false) }
  }
  return <div className="fb-layer" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
    <div ref={card} className="fb-sheet" role="dialog" aria-modal="true" aria-labelledby="fb-title">
      <button type="button" className="fb-close" aria-label={L('Close', 'Cerrar')} onClick={onClose}><Icon name="close" size={18}/></button>
      {sent ? <>
        <h2 id="fb-title">{L('Thank you', 'Gracias')}</h2>
        <p>{L('We read every note. When we do something about it, you will see it in your inbox.', 'Leemos cada nota. Cuando hagamos algo al respecto, lo verás en tu buzón.')}</p>
        <div className="fb-actions"><a className="fb-primary" href="#/feedback" onClick={onClose}>{L('See your feedback', 'Ver tus comentarios')}</a><button type="button" onClick={onClose}>{L('Close', 'Cerrar')}</button></div>
      </> : <>
        <h2 id="fb-title">{L('Tell us how it is going', 'Cuéntanos cómo te va')}</h2>
        <div role="radiogroup" aria-label={L('What is it?', '¿De qué se trata?')} className="fb-kinds">
          {KINDS.map(k => <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>{kindLabel(k)}</button>)}
        </div>
        <Stars value={rating} onPick={setRating} label={L('How is it going so far? (optional)', '¿Cómo te va hasta ahora? (opcional)')}/>
        <label className="fb-field">{L('Tell us more', 'Cuéntanos más')}
          <textarea value={message} maxLength={1000} rows={5} onChange={e => setMessage(e.target.value)} placeholder={L('What happened, or what would you like?', '¿Qué pasó, o qué te gustaría?')}/>
          <small>{message.length} / 1000 · {L('We attach the page you are on and the app version, nothing else.', 'Adjuntamos la página donde estás y la versión de la app, nada más.')}</small>
        </label>
        {error && <p role="alert" className="fb-error">{error}</p>}
        <div className="fb-actions"><button type="button" className="fb-primary" disabled={!kind || message.trim().length < 3 || busy} onClick={() => void send()}>{busy ? '…' : L('Send', 'Enviar')}</button><button type="button" onClick={onClose}>{L('Cancel', 'Cancelar')}</button></div>
      </>}
    </div>
  </div>
}

/** The floating button, and the form it opens. Shown to signed-in people only. */
export function FeedbackButton({ hidden = false }: { hidden?: boolean }) {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const on = () => setOpen(true)
    window.addEventListener(OPEN, on)
    return () => window.removeEventListener(OPEN, on)
  }, [])
  return <>
    {!hidden && <button type="button" className="fb-fab" aria-haspopup="dialog" aria-label={L('Feedback', 'Comentarios')} title={L('Feedback', 'Comentarios')} onClick={() => setOpen(true)}><Icon name="chat" size={20}/></button>}
    {open && <Sheet onClose={() => setOpen(false)}/>}
  </>
}

type Mine = { id: string; kind: Kind; rating: number | null; message: string; status: Status; reply: string; repliedAt: number | null; createdAt: number }
/** What this person has sent, and what happened to each. */
export function FeedbackPage() {
  const [rows, setRows] = useState<Mine[] | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => { void get<{ feedback: Mine[] }>('/api/market/feedback/mine').then(r => setRows(r.feedback)).catch(() => setFailed(true)) }, [])
  return <section className="fb-page">
    <h1>{L('Your feedback', 'Tus comentarios')}</h1>
    <p>{L('Everything you have sent us, and what happened to it.', 'Todo lo que nos has enviado y lo que pasó con ello.')}</p>
    <button type="button" className="market-button coral" onClick={openFeedback}><Icon name="plus"/>{L('Send feedback', 'Enviar un comentario')}</button>
    {failed && <p role="alert" className="market-empty">{L('We could not load this. Please try again.', 'No pudimos cargar esto. Inténtalo de nuevo.')}</p>}
    {rows && rows.length === 0 && <p className="market-empty">{L('You have not sent any feedback yet. We would love to hear how it is going.', 'Aún no has enviado comentarios. Nos encantaría saber cómo te va.')}</p>}
    <ul className="fb-list">{rows?.map(r => <li key={r.id} className="fb-item">
      <div className="fb-head"><strong>{kindLabel(r.kind)}</strong><span className={`fb-status fb-${r.status}`}>{statusLabel(r.status)}</span><small>{day(r.createdAt)}</small></div>
      {r.rating !== null && <Stars value={r.rating} label=""/>}
      <p className="fb-text">{r.message}</p>
      {r.reply && <blockquote className="fb-reply"><strong>{L('From the team', 'Del equipo')}</strong><p>{r.reply}</p></blockquote>}
    </li>)}</ul>
  </section>
}

type Inbox = Mine & { handle: string; name: string; page: string; version: string }
type Counts = Record<Status, number>
/** The owner's and admins' inbox: read, set a status, reply. The person is told in their app inbox. */
export function FeedbackReview() {
  const [rows, setRows] = useState<Inbox[] | null>(null)
  const [counts, setCounts] = useState<Counts | null>(null)
  const [status, setStatus] = useState<Status | ''>('')
  const [kind, setKind] = useState<Kind | ''>('')
  const [failed, setFailed] = useState(false)
  const load = useCallback(() => {
    const q = new URLSearchParams(); if (status) q.set('status', status); if (kind) q.set('kind', kind)
    void get<{ feedback: Inbox[]; counts: Counts }>(`/api/market/feedback${q.size ? `?${q}` : ''}`).then(r => { setRows(r.feedback); setCounts(r.counts); setFailed(false) }).catch(() => setFailed(true))
  }, [status, kind])
  useEffect(load, [load])
  const total = counts ? STATUSES.reduce((n, s) => n + counts[s], 0) : 0
  return <section className="fb-page fb-review">
    <h1>{L('Feedback inbox', 'Buzón de comentarios')}</h1>
    <div className="fb-tabs" role="group" aria-label={L('Show', 'Mostrar')}>
      <button type="button" aria-pressed={status === ''} onClick={() => setStatus('')}>{L('All', 'Todos')} <b>{total}</b></button>
      {STATUSES.map(s => <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)}>{statusLabel(s)} <b>{counts?.[s] ?? 0}</b></button>)}
    </div>
    <label className="fb-filter">{L('Kind', 'Tipo')}
      <select value={kind} onChange={e => setKind(e.target.value as Kind | '')}><option value="">{L('Every kind', 'Todos los tipos')}</option>{KINDS.map(k => <option key={k} value={k}>{kindLabel(k)}</option>)}</select>
    </label>
    {failed && <p role="alert" className="market-empty">{L('We could not load this. Please try again.', 'No pudimos cargar esto. Inténtalo de nuevo.')}</p>}
    {rows && rows.length === 0 && <p className="market-empty">{L('Nothing here.', 'Nada por aquí.')}</p>}
    <ul className="fb-list">{rows?.map(r => <ReviewItem key={r.id} row={r} onSaved={load}/>)}</ul>
  </section>
}

function ReviewItem({ row, onSaved }: { row: Inbox; onSaved: () => void }) {
  const [next, setNext] = useState<Status>(row.status)
  const [reply, setReply] = useState(row.reply)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const dirty = next !== row.status || reply.trim() !== row.reply
  const save = async () => {
    setBusy(true); setNote('')
    try { await api(`/api/market/feedback/${row.id}`, { method: 'POST', body: JSON.stringify({ status: next, reply }) }); setNote(L('Saved. They have been told.', 'Guardado. Ya se les avisó.')); onSaved() } catch (e) { setNote(errorText(e)) } finally { setBusy(false) }
  }
  return <li className="fb-item">
    <div className="fb-head"><strong>{kindLabel(row.kind)}</strong><span className={`fb-status fb-${row.status}`}>{statusLabel(row.status)}</span><small>{day(row.createdAt)} · {row.name || row.handle} (@{row.handle})</small></div>
    {row.rating !== null && <Stars value={row.rating} label=""/>}
    <p className="fb-text">{row.message}</p>
    <p className="fb-meta">{row.page || '—'} · {row.version}</p>
    <div className="fb-work">
      <label>{L('Status', 'Estado')}<select value={next} onChange={e => setNext(e.target.value as Status)}>{STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}</select></label>
      <label className="fb-grow">{L('Reply (they will see it)', 'Respuesta (la verán)')}<textarea rows={2} maxLength={600} value={reply} onChange={e => setReply(e.target.value)}/></label>
      <button type="button" className="fb-primary" disabled={!dirty || busy} onClick={() => void save()}>{busy ? '…' : L('Save', 'Guardar')}</button>
    </div>
    {note && <p role="status" className="fb-note">{note}</p>}
  </li>
}
